import { useCallback, useRef } from 'react';

import * as Sentry from '@sentry/browser';
import { severityLevelFromString } from '@sentry/utils';

import { httpErrorResponse } from '../requests/shared';

type LogMessage = {
    message: string;
    level: Sentry.SeverityLevel;
    error?: any;
    statusCode?: string;
};

const getLogObject = (
    level: Sentry.SeverityLevel,
    appMessage: string,
    error?: any,
): LogMessage => ({
    message: appMessage,
    level: severityLevelFromString(level),
    error:
        JSON.stringify(error?.data || error?.message || error) ||
        'unknown error',
    statusCode: `${error?.status || 'N/A'}`,
});

const isSystemError = (statusCode?: string) => {
    const code = Number(statusCode);
    return statusCode === 'N/A' || Number.isNaN(code) || code >= 500;
};

export const useSentryLogger = (appId: string, location: string) => {
    const title = 'User story - ' + appId;
    const log = useRef<LogMessage[]>([]);

    const flushLogs = useCallback(() => {
        if (log.current.length > 0) {
            const lastEntry = log.current[log.current.length - 1];
            const systemError =
                lastEntry.level === 'error' &&
                isSystemError(lastEntry.statusCode);
            const extra = {
                logs: log.current.map((logRow) => logRow.message).join('\n'),
                error: lastEntry?.error || '',
                location: location,
                statusCode: lastEntry?.statusCode,
            };
            try {
                Sentry.captureEvent({
                    message: systemError ? `${title} (system error)` : title,
                    level: severityLevelFromString(lastEntry.level),
                    ...(systemError && {
                        tags: {
                            statusCode: lastEntry.statusCode,
                            errorClass: 'system',
                        },
                        fingerprint: [title, 'system-error'],
                    }),
                    extra,
                });
            } catch {}
            log.current = [];
        }
    }, [location, title, log]);

    const sentryWarning = useCallback(
        (appMessage: string, error?: any, callBack?: Function) => {
            log.current.push(getLogObject('warning', appMessage, error));
            if (callBack) callBack();
        },
        [],
    );

    const sentryError = useCallback(
        (appMessage: string, error?: any, callBack?: Function) => {
            log.current.push(getLogObject('error', appMessage, error));
            if (callBack) callBack();
        },
        [],
    );

    const sentryLog = useCallback((appMessage: string, callBack?: Function) => {
        log.current.push(getLogObject('info', appMessage, {}));
        if (callBack) callBack();
    }, []);

    const handleError = useCallback(
        (error: any, step: string) => {
            sentryError(`Error on ${step}`, error);
            flushLogs();
            return httpErrorResponse(error);
        },
        [flushLogs, sentryError],
    );

    return {
        sentryError,
        sentryLog,
        sentryWarning,
        flushLogs,
        handleError,
    };
};
